import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, Link } from 'react-router-dom';

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [currentLang, setCurrentLang] = useState(i18n.language || 'ru');
  const [isLangDropdownOpen, setIsLangDropdownOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setIsMobileMenuOpen(false);
    }
  };

  const changeLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
    setCurrentLang(lang);
    setIsLangDropdownOpen(false);
    
    // Navigate to appropriate page
    if (lang === 'en') {
      navigate('/en');
    } else {
      navigate('/');
    }
  };

  const languages = [
    { code: 'ru', label: 'RU', flag: '🇷🇺', name: 'Русский' },
    { code: 'en', label: 'EN', flag: '🇬🇧', name: 'English' }
  ];

  const currentLanguage = languages.find(lang => lang.code === currentLang) || languages[0];

  // Determine company name based on current language
  const companyName = currentLang === 'en' ? 'Systematic Marketing' : 'Системный маркетинг';

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white shadow-md`}
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            <div className="w-12 h-12 rounded-xl overflow-hidden flex items-center justify-center bg-white">
              <img 
                src="https://static.readdy.ai/image/621f76ab0c5d0ae7827482fe9856efef/d957a6b7765ec2d313d91cb8edd75d6a.png" 
                alt={companyName} 
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <div className="text-xl font-bold text-premium-navy font-display">
                {companyName}
              </div>
            </div>
          </div>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center gap-8">
            <button
              onClick={() => scrollToSection('why-us')}
              className="font-medium transition-colors whitespace-nowrap text-premium-navy hover:text-premium-gold font-body"
            >
              {t('nav.about')}
            </button>
            <button
              onClick={() => scrollToSection('how-we-work')}
              className="font-medium transition-colors whitespace-nowrap text-premium-navy hover:text-premium-gold font-body"
            >
              {t('nav.approach')}
            </button>
            <Link
              to="/case"
              className="font-medium transition-colors whitespace-nowrap text-premium-navy hover:text-premium-gold font-body"
            >
              Кейсы
            </Link>
            <button
              onClick={() => scrollToSection('services')}
              className="font-medium transition-colors whitespace-nowrap text-premium-navy hover:text-premium-gold font-body"
            >
              {t('nav.services')}
            </button>
            <button
              onClick={() => scrollToSection('contacts')}
              className="font-medium transition-colors whitespace-nowrap text-premium-navy hover:text-premium-gold font-body"
            >
              {t('nav.contacts')}
            </button>

            {/* Language Dropdown */}
            <div className="relative ml-4">
              <button
                onClick={() => setIsLangDropdownOpen(!isLangDropdownOpen)}
                className="px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap flex items-center gap-2 bg-premium-pearl text-premium-navy hover:bg-premium-gold hover:text-white font-body"
              >
                <span>{currentLanguage.flag}</span>
                <span>{currentLanguage.label}</span>
                <i className={`ri-arrow-down-s-line text-lg transition-transform ${isLangDropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {/* Dropdown Menu */}
              {isLangDropdownOpen && (
                <div className="absolute top-full right-0 mt-2 bg-white rounded-lg shadow-xl overflow-hidden min-w-[160px] animate-fadeIn border border-premium-pearl">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={`w-full px-4 py-3 text-left flex items-center gap-3 transition-colors whitespace-nowrap font-body ${
                        currentLang === lang.code
                          ? 'bg-premium-navy text-white'
                          : 'text-premium-navy hover:bg-premium-pearl'
                      }`}
                    >
                      <span className="text-xl">{lang.flag}</span>
                      <div>
                        <div className="font-medium">{lang.label}</div>
                        <div className="text-xs opacity-70">{lang.name}</div>
                      </div>
                      {currentLang === lang.code && (
                        <i className="ri-check-line text-premium-gold ml-auto"></i>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center rounded-lg text-premium-navy hover:bg-premium-pearl"
          >
            <i className={`text-2xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-white rounded-2xl shadow-2xl mb-4 overflow-hidden animate-fadeIn border border-premium-pearl">
            <div className="py-4">
              <button
                onClick={() => scrollToSection('why-us')}
                className="w-full px-6 py-3 text-left text-premium-navy hover:bg-premium-pearl hover:text-premium-gold transition-colors font-medium font-body"
              >
                {t('nav.about')}
              </button>
              <button
                onClick={() => scrollToSection('how-we-work')}
                className="w-full px-6 py-3 text-left text-premium-navy hover:bg-premium-pearl hover:text-premium-gold transition-colors font-medium font-body"
              >
                {t('nav.approach')}
              </button>
              <Link
                to="/case"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block w-full px-6 py-3 text-left text-premium-navy hover:bg-premium-pearl hover:text-premium-gold transition-colors font-medium font-body"
              >
                Кейсы
              </Link>
              <button
                onClick={() => scrollToSection('services')}
                className="w-full px-6 py-3 text-left text-premium-navy hover:bg-premium-pearl hover:text-premium-gold transition-colors font-medium font-body"
              >
                {t('nav.services')}
              </button>
              <button
                onClick={() => scrollToSection('contacts')}
                className="w-full px-6 py-3 text-left text-premium-navy hover:bg-premium-pearl hover:text-premium-gold transition-colors font-medium font-body"
              >
                {t('nav.contacts')}
              </button>
              
              {/* Mobile Language Switcher */}
              <div className="px-6 py-3 border-t border-premium-pearl mt-2">
                <div className="text-sm text-gray-500 mb-2 font-body">Язык / Language</div>
                <div className="grid grid-cols-2 gap-2">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={`px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap font-body ${
                        currentLang === lang.code
                          ? 'bg-premium-navy text-white'
                          : 'bg-premium-pearl text-premium-navy hover:bg-premium-gold hover:text-white'
                      }`}
                    >
                      <span className="mr-2">{lang.flag}</span>
                      {lang.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}